const feature = { name: 'consent-tracking-api', version: '0.1' };
const cookieCategories = ['preferences', 'analytics', 'marketing'];

function loadConsentApi() {
  return new Promise((resolve, reject) => {
    if (!window.Shopify?.loadFeatures) {
      reject(new Error('Shopify feature loader is unavailable'));
      return;
    }

    window.Shopify.loadFeatures([feature], (error) => {
      if (error) reject(error);
      else if (window.Shopify.customerPrivacy) resolve(window.Shopify.customerPrivacy);
      else reject(new Error('Shopify Customer Privacy API did not load'));
    });
  });
}

function setConsent(api, values) {
  return new Promise((resolve) => {
    api.setTrackingConsent(values, resolve);
  });
}

async function initializeConsent() {
  const banner = document.querySelector('[data-freya-cookie-banner]');
  const dialog = document.querySelector('[data-freya-cookie-dialog]');
  if (!banner || !dialog) return;

  let api;
  try {
    api = await loadConsentApi();
  } catch (error) {
    console.error('Freya cookie consent could not load Shopify Customer Privacy API.', error);
    return;
  }

  // Replace Shopify's injected presentation only after the supported API is ready.
  document.documentElement.classList.add('freya-cookie-consent-ready');
  const hasConsent = (value) => value === 'yes' || value === true;
  const inputs = new Map(
    [...dialog.querySelectorAll('[data-freya-cookie-category]')].map((input) => [input.dataset.freyaCookieCategory, input]),
  );
  const saleOption = dialog.querySelector('[data-freya-cookie-sale-option]');
  const saleRegion = api.saleOfDataRegion?.();
  if (saleOption && saleRegion) saleOption.hidden = false;

  const bannerRequired = api.shouldShowBanner();
  const visitorConsent = api.currentVisitorConsent() || {};
  // Data-sale opt-out is separate from cookie consent and must not satisfy the banner gate.
  const hasCookieChoice = cookieCategories.some((category) => visitorConsent[category] === 'yes' || visitorConsent[category] === 'no');
  banner.hidden = !(bannerRequired && !hasCookieChoice);

  const manage = banner.querySelector('[data-freya-cookie-manage]');
  let saleOfDataInitialValue = false;
  const syncInputs = () => {
    const current = api.currentVisitorConsent() || {};
    for (const category of cookieCategories) {
      const input = inputs.get(category);
      if (input) input.checked = hasConsent(current[category]);
    }
    const saleInput = inputs.get('sale_of_data');
    if (saleInput) {
      saleInput.checked = current.sale_of_data === 'no';
      saleOfDataInitialValue = saleInput.checked;
    }
  };

  let dialogTrigger;
  const openPreferences = (trigger) => {
    dialogTrigger = trigger;
    syncInputs();
    if (!dialog.open) dialog.showModal();
  };
  manage?.addEventListener('click', (event) => openPreferences(event.currentTarget));
  document.addEventListener('click', (event) => {
    const settingsLink = event.target.closest('[data-freya-cookie-settings]');
    if (!settingsLink) return;
    event.preventDefault();
    openPreferences(settingsLink);
  });

  const collectCookieChoice = (accepted) => Object.fromEntries(
    cookieCategories.map((category) => [category, accepted]),
  );
  const save = async (cookieValues, { updateSaleOptOut = false } = {}) => {
    const buttons = [...banner.querySelectorAll('button'), ...dialog.querySelectorAll('button')];
    buttons.forEach((button) => { button.disabled = true; });
    try {
      await setConsent(api, cookieValues);
      const saleInput = inputs.get('sale_of_data');
      if (updateSaleOptOut && saleInput && !saleOption.hidden && saleInput.checked !== saleOfDataInitialValue) {
        // The checkbox means opt out; write only this purpose, independently from cookie consent.
        await setConsent(api, { sale_of_data: !saleInput.checked });
      }
      banner.hidden = true;
      if (dialog.open) dialog.close();
    } catch (error) {
      console.error('Freya cookie consent could not be saved by Shopify.', error);
    } finally {
      buttons.forEach((button) => { button.disabled = false; });
    }
  };

  banner.querySelector('[data-freya-cookie-accept]')?.addEventListener('click', () => save(collectCookieChoice(true)));
  banner.querySelector('[data-freya-cookie-reject]')?.addEventListener('click', () => save(collectCookieChoice(false)));
  dialog.querySelector('[data-freya-cookie-save]')?.addEventListener('click', () => {
    save(Object.fromEntries(cookieCategories.map((category) => [category, inputs.get(category)?.checked ?? false])), {
      updateSaleOptOut: true,
    });
  });

  dialog.addEventListener('close', () => {
    const focusTarget = banner.hidden ? document.querySelector('[data-freya-cookie-settings]') : dialogTrigger;
    focusTarget?.focus();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeConsent, { once: true });
} else {
  initializeConsent();
}
